import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Loader from '../../shared/components/ui/Loader';
import Modal from '../../shared/components/ui/Modal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { Eye, History, Pencil, Play, Plus, Save, Search, Trash2, X } from 'lucide-react';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import toast from 'react-hot-toast';
import Select from 'react-select';
import {
  createWhatsAppQuestions,
  getPreLeadLists,
  getPreLeadsByListId,
  getWhatsAppCampaignRuns,
  getWhatsAppCampaigns,
  getWhatsAppQuestions,
  getWhatsAppTemplates,
  startWhatsAppCampaign,
  updateWhatsAppQuestions
} from '../services/api/implementation/impl';
import { useWhatsAppCampaignsBasePath } from '../hooks/useWhatsAppCampaignsBasePath';

const PAGE_BG = '#F8F9FA';
/** Preview app bar — matches questionnaire mock */
const WA_PREVIEW_HEADER = '#336B6B';
/** Chat canvas behind cards — light neutral */
const WA_CHAT_BG = '#F5F5F5';
const WA_LINK_BLUE = '#027EB5';
const WA_OPTION_BG = '#E8F4FC';
const WA_CARD_BORDER = '#E5E7EB';

function statusBadgeClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING' || s === 'IN_REVIEW') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (s === 'APPROVED' || s === 'ACTIVE')
    return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (s === 'REJECTED' || s === 'DISABLED') return 'bg-rose-50 text-rose-800 border-rose-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
}

function normalizeCampaignStatus(raw) {
  const s = String(raw || '').trim().toLowerCase();
  return ['draft', 'active', 'completed'].includes(s) ? s : '';
}

function createQuestionId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function initialsFromName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return 'WA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function normalizeComponentsArray(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  if (typeof raw === 'object' && Array.isArray(raw.components)) return raw.components;
  return [];
}

function componentTypeUpper(c) {
  return String(c?.type ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
}

/** BODY / HEADER text can arrive as `text`, `body`, Meta `example.body_text`, etc. */
function extractComponentText(comp) {
  if (!comp || typeof comp !== 'object') return '';
  if (typeof comp.text === 'string' && comp.text.trim()) return comp.text.trim();
  if (typeof comp.body === 'string' && comp.body.trim()) return comp.body.trim();
  if (typeof comp.body_text === 'string' && comp.body_text.trim()) return comp.body_text.trim();
  const ex = comp.example;
  if (ex && Array.isArray(ex.body_text)) {
    const row = ex.body_text[0];
    if (Array.isArray(row) && row[0] != null && String(row[0]).trim()) return String(row[0]).trim();
    if (typeof row === 'string' && row.trim()) return row.trim();
  }
  return '';
}

function getTemplateComponentsList(template) {
  if (!template || typeof template !== 'object') return [];
  const fromFields = [
    template.components,
    template.Components,
    template.template_components,
    template.template?.components,
    template.payload?.components,
    template.whatsapp_template?.components
  ];
  for (const field of fromFields) {
    const arr = normalizeComponentsArray(field);
    if (arr.length) return arr;
  }
  if (typeof template.body === 'string' && template.body.trim()) {
    return [{ type: 'BODY', text: template.body.trim() }];
  }
  if (typeof template.message === 'string' && template.message.trim()) {
    return [{ type: 'BODY', text: template.message.trim() }];
  }
  if (typeof template.body_text === 'string' && template.body_text.trim()) {
    return [{ type: 'BODY', text: template.body_text.trim() }];
  }
  return [];
}

/** Intro message + quick-reply labels from stored WhatsApp template (GET shape may vary). */
function parseTemplateForPreview(template) {
  const comps = getTemplateComponentsList(template);
  const parts = [];

  const header = comps.find((c) => componentTypeUpper(c) === 'HEADER');
  const headerText = extractComponentText(header);
  if (headerText) parts.push(headerText);

  const bodyComp = comps.find((c) => componentTypeUpper(c) === 'BODY');
  const bodyText = extractComponentText(bodyComp);
  if (bodyText) parts.push(bodyText);

  const footer = comps.find((c) => componentTypeUpper(c) === 'FOOTER');
  if (typeof footer?.text === 'string' && footer.text.trim()) parts.push(footer.text.trim());
  else if (extractComponentText(footer)) parts.push(extractComponentText(footer));

  const btnBlock = comps.find((c) => componentTypeUpper(c) === 'BUTTONS');
  const buttons = Array.isArray(btnBlock?.buttons)
    ? btnBlock.buttons
        .map((b) => String(b?.text ?? b?.title ?? b?.label ?? '').trim())
        .filter(Boolean)
    : [];

  return {
    introBody: parts.join('\n\n').trim(),
    buttons
  };
}

const MIN_QUICK_REPLY_OPTIONS = 2;
const MAX_QUICK_REPLY_OPTIONS = 4;
/** WhatsApp quick-reply button title limit */
const MAX_QUICK_REPLY_BUTTON_CHARS = 20;
/** API `category` — fixed default per product request */
const WHATSAPP_QUESTIONS_CATEGORY_DEFAULT = 'default';

function slugifyOptionPayload(optionLabel, sortOrder) {
  const raw = String(optionLabel || '')
    .trim()
    .normalize('NFKC')
    .toLowerCase()
    .replace(/₹/g, 'inr')
    .replace(/[\s–—-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
  if (raw) return raw.slice(0, 96);
  return `option_${sortOrder}`;
}

function optionLabel(opt) {
  if (opt == null) return '';
  if (typeof opt === 'string') return String(opt);
  return String(opt.label ?? '');
}

function buildWhatsAppQuestionsPayload(whatsappCampaignId, questions, category) {
  const cat = String(category || '').trim() || WHATSAPP_QUESTIONS_CATEGORY_DEFAULT;
  return {
    whatsapp_campaign_id: Number(whatsappCampaignId),
    category: cat,
    questions: questions.map((q, idx) => ({
      question_order: idx + 1,
      prompt_text: String(q.prompt || '').trim() || `Question ${idx + 1}`,
      options: q.options.map((opt, i) => {
        const trimmed = String(optionLabel(opt)).trim().slice(0, MAX_QUICK_REPLY_BUTTON_CHARS);
        return {
          label: trimmed || `Option ${i + 1}`,
          payload: slugifyOptionPayload(trimmed, i),
          sort_order: i
        };
      })
    }))
  };
}

/** PUT `/whatsappQuestions/bulk` — include `id` only for rows that already exist on the server. */
function buildWhatsAppQuestionsBulkPayload(whatsappCampaignId, questions, category) {
  const cat = String(category || '').trim() || WHATSAPP_QUESTIONS_CATEGORY_DEFAULT;
  return {
    whatsapp_campaign_id: Number(whatsappCampaignId),
    category: cat,
    questions: questions.map((q, idx) => {
      const promptText = String(q.prompt || '').trim() || `Question ${idx + 1}`;
      const options = q.options.map((opt, i) => {
        const trimmed = String(optionLabel(opt)).trim().slice(0, MAX_QUICK_REPLY_BUTTON_CHARS);
        const label = trimmed || `Option ${i + 1}`;
        const payload =
          typeof opt === 'object' &&
          opt != null &&
          typeof opt.payload === 'string' &&
          opt.payload.trim()
            ? String(opt.payload).trim().slice(0, 96)
            : slugifyOptionPayload(trimmed, i);
        const base = { label, payload, sort_order: i };
        const sid =
          typeof opt === 'object' && opt != null && opt.serverId != null
            ? Number(opt.serverId)
            : NaN;
        if (Number.isFinite(sid)) return { id: sid, ...base };
        return base;
      });
      const qBase = {
        question_order: idx + 1,
        prompt_text: promptText,
        options
      };
      const qid = q.serverId != null ? Number(q.serverId) : NaN;
      if (Number.isFinite(qid)) return { id: qid, ...qBase };
      return qBase;
    })
  };
}

/** DB id for POST /WhatsAppCampaign/:id/start — from GET templates `data[].whatsapp_template.id` */
function resolveWhatsAppTemplateId(template) {
  if (!template || typeof template !== 'object') return null;
  const nested = template.whatsapp_template;
  const raw =
    (nested && typeof nested === 'object' ? nested.id : null) ??
    template.whatsapp_template_id ??
    template.whatsappTemplate?.id ??
    template.id ??
    template.template_id ??
    template.whatsappTemplateId;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function mapApiQuestionsToLocalState(apiRows) {
  if (!Array.isArray(apiRows) || apiRows.length === 0) return null;
  return [...apiRows]
    .sort((a, b) => (Number(a.question_order) || 0) - (Number(b.question_order) || 0))
    .map((row) => {
      const opts = Array.isArray(row.options) ? [...row.options] : [];
      opts.sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
      const options = opts.map((o) => {
        const label = String(o.label ?? '')
          .trim()
          .slice(0, MAX_QUICK_REPLY_BUTTON_CHARS);
        const oid = Number(o.id);
        const serverId = Number.isFinite(oid) ? oid : null;
        const payloadRaw = o.payload != null ? String(o.payload).trim() : '';
        return {
          label: label || 'Option',
          serverId,
          ...(payloadRaw ? { payload: payloadRaw.slice(0, 96) } : {})
        };
      });
      while (options.length < MIN_QUICK_REPLY_OPTIONS) {
        options.push({ label: `Option ${options.length + 1}`, serverId: null });
      }
      const qid = Number(row.id);
      return {
        id: createQuestionId(),
        serverId: Number.isFinite(qid) ? qid : null,
        prompt: String(row.prompt_text ?? row.prompt ?? '').trim() || 'Question',
        options
      };
    });
}

const DEFAULT_QUESTIONS = () => [
  {
    id: createQuestionId(),
    serverId: null,
    prompt: 'What is your budget range?',
    options: [
      { label: 'Below ₹ 50L', serverId: null },
      { label: '₹ 50L – ₹ 1Cr', serverId: null },
      { label: 'Above ₹ 1Cr', serverId: null }
    ]
  },
  {
    id: createQuestionId(),
    serverId: null,
    prompt: 'What kind of property are you looking for?',
    options: [
      { label: 'Flat', serverId: null },
      { label: 'Villa / ind. house', serverId: null }
    ]
  }
];

function QuestionCard({ index, question, onPatch, onRemove, readOnly }) {
  const setPrompt = (prompt) => onPatch({ prompt });
  const setOptionAt = (i, text) => {
    const capped = String(text).slice(0, MAX_QUICK_REPLY_BUTTON_CHARS);
    const next = question.options.map((o, j) => {
      if (j !== i) return o;
      if (typeof o === 'string') return { label: capped, serverId: null };
      return { ...o, label: capped };
    });
    onPatch({ options: next });
  };
  const addOption = () => {
    if (question.options.length >= MAX_QUICK_REPLY_OPTIONS) return;
    onPatch({ options: [...question.options, { label: 'New option', serverId: null }] });
  };
  const removeOption = (i) => {
    if (question.options.length <= MIN_QUICK_REPLY_OPTIONS) return;
    onPatch({ options: question.options.filter((_, j) => j !== i) });
  };

  const canRemoveOption = question.options.length > MIN_QUICK_REPLY_OPTIONS;
  const canAddOption = question.options.length < MAX_QUICK_REPLY_OPTIONS;

  if (readOnly) {
    return (
      <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-100 px-4 py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-800">
            {index + 1}
          </span>
          <p className="min-w-0 flex-1 text-base font-semibold leading-snug text-gray-900">
            {question.prompt || '—'}
          </p>
        </div>
        <div className="p-4">
          <p className="mb-2 text-xs font-medium text-gray-600">Quick reply options</p>
          <ul className="space-y-1.5 text-sm text-gray-800">
            {question.options.map((opt, i) => (
              <li key={i} className="flex gap-2">
                <span className="shrink-0 text-gray-400">•</span>
                <span>{optionLabel(opt) || '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      </article>
    );
  }

  return (
    <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-100 px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-800">
          {index + 1}
        </span>
        <input
          type="text"
          value={question.prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="min-w-0 flex-1 border-0 bg-transparent text-base font-semibold text-gray-900 outline-none ring-0 placeholder:text-gray-400"
          placeholder="Question text"
        />
        <button
          type="button"
          onClick={onRemove}
          className="rounded-full p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-rose-600"
          aria-label="Remove question">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="p-4">
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs font-medium text-gray-600">Quick Reply Buttons</p>
              <p className="text-[11px] text-gray-400">
                {MIN_QUICK_REPLY_OPTIONS}–{MAX_QUICK_REPLY_OPTIONS} options · max{' '}
                {MAX_QUICK_REPLY_BUTTON_CHARS} characters each
              </p>
            </div>
            {canAddOption ? (
              <button
                type="button"
                onClick={addOption}
                className="text-sm font-semibold text-[#027EB5] hover:underline">
                + Add
              </button>
            ) : null}
          </div>
          <ul className="space-y-2">
            {question.options.map((opt, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="shrink-0 text-gray-400">•</span>
                <input
                  type="text"
                  value={optionLabel(opt)}
                  maxLength={MAX_QUICK_REPLY_BUTTON_CHARS}
                  onChange={(e) => setOptionAt(i, e.target.value)}
                  className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none ring-[#346C75]/30 placeholder:text-gray-400 focus:border-transparent focus:ring-2"
                  placeholder="Button label"
                />
                <button
                  type="button"
                  onClick={() => removeOption(i)}
                  disabled={!canRemoveOption}
                  className="shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-rose-600 disabled:pointer-events-none disabled:opacity-30"
                  aria-label="Remove option">
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}

function QuestionnaireWhatsAppPreview({ barTitle, initials, introBody, introButtons, questions }) {
  const timeLabel = useMemo(
    () =>
      new Date().toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
    []
  );

  const cardClass =
    'w-full rounded-[14px] border bg-white px-[18px] py-[18px] shadow-sm antialiased';
  const cardStyle = { borderColor: WA_CARD_BORDER };

  return (
    <div className="flex h-full min-h-0 flex-col border-l border-gray-200 bg-[#ECECEC]">
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-200/90 bg-gray-50 px-4 py-2.5">
        <Eye className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        <span className="text-sm font-semibold tracking-tight text-gray-700">Preview</span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4 sm:items-center">
        <div className="flex min-h-0 w-full max-w-[340px] flex-1 flex-col overflow-hidden rounded-[20px] border border-gray-300 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.08)] sm:mx-auto">
          <header
            className="flex shrink-0 items-center gap-3 rounded-t-[20px] px-4 py-3.5"
            style={{ backgroundColor: WA_PREVIEW_HEADER }}>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-sm font-bold text-[#336B6B] shadow-sm">
              {initials}
            </span>
            <span className="min-w-0 truncate text-base font-bold leading-tight text-white">
              {barTitle}
            </span>
          </header>
          <div
            className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-4"
            style={{ backgroundColor: WA_CHAT_BG }}>
            <div className="mx-auto flex w-full max-w-[300px] flex-col gap-4">
              {(introBody || introButtons.length > 0) && (
                <article className={cardClass} style={cardStyle}>
                  {introBody ? (
                    <p className="whitespace-pre-wrap text-[15px] font-normal leading-relaxed text-gray-800">
                      {introBody}
                    </p>
                  ) : null}
                  <p className="mt-2 text-right text-xs font-normal text-gray-400">{timeLabel}</p>
                  {introButtons.length > 0 ? (
                    <div className="mt-3 border-t border-gray-200">
                      <div className="divide-y divide-gray-200">
                        {introButtons.map((label, i) => (
                          <div
                            key={i}
                            className="py-3 text-center text-[15px] font-medium"
                            style={{ color: WA_LINK_BLUE }}>
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </article>
              )}

              {questions.map((q) => (
                <article key={q.id} className={cardClass} style={cardStyle}>
                  <p className="text-[15px] font-medium leading-snug text-gray-800">{q.prompt}</p>
                  {q.options.filter((t) => String(optionLabel(t)).trim()).length > 0 ? (
                    <div className="mt-4 flex flex-col gap-2">
                      {q.options
                        .map((t) => String(optionLabel(t)).trim())
                        .filter(Boolean)
                        .map((label, i) => (
                          <div
                            key={i}
                            className="rounded-[10px] py-2.5 text-center text-sm font-medium"
                            style={{
                              backgroundColor: WA_OPTION_BG,
                              color: WA_LINK_BLUE
                            }}>
                            {label}
                          </div>
                        ))}
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Pre-lead list picker in Start campaign modal (native select cannot cap dropdown height). */
const startCampaignPreLeadListSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#346C75' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(52, 108, 117, 0.25)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#346C75' : '#9ca3af' }
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10001 }),
  menu: (base) => ({
    ...base,
    zIndex: 10001,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.08)'
  }),
  menuList: (base) => ({ ...base, padding: 0 })
};

function formatRunDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '—';
  }
}

function runStatusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active' || s === 'completed' || s === 'success')
    return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (s === 'failed' || s === 'error') return 'bg-rose-50 text-rose-800 border-rose-200';
  if (s === 'pending' || s === 'processing') return 'bg-amber-50 text-amber-800 border-amber-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
}

function getRunDeliverySummary(run) {
  const summary = run?.metadata?.data?.data?.summary;
  return {
    total: summary?.totalContacts ?? run?.recipient_count ?? 0,
    sent: summary?.sentSuccessfully ?? run?.success_count ?? 0,
    failed: summary?.failed ?? 0,
    skipped: run?.skipped_invalid_phone ?? 0
  };
}

function filterPreLeadsByRecipientSearch(preLeads, queryRaw) {
  const query = String(queryRaw ?? '').trim().toLowerCase();
  if (!query) return preLeads;
  const queryDigits = query.replace(/\D/g, '');
  return preLeads.filter((p) => {
    const name = String(p?.name ?? '').toLowerCase();
    const email = String(p?.email ?? '').toLowerCase();
    const phoneDigits = String(p?.phone ?? '').replace(/\D/g, '');
    const line = `${String(p?.countryCode ?? '')} ${String(p?.phone ?? '')}`
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
    if (name.includes(query) || email.includes(query) || line.includes(query)) return true;
    if (queryDigits.length >= 2 && phoneDigits.includes(queryDigits)) return true;
    return false;
  });
}

const WhatsAppLeadQuestionnaire = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams();
  const { collapsed } = useSidebar();
  const userId = Cookies.get('userId');
  const basePath = useWhatsAppCampaignsBasePath();
  const queryClient = useQueryClient();
  const hydratedCampaignRef = useRef(null);
  const questionnairePaneRef = useRef(null);
  const [questionnairePaneHeight, setQuestionnairePaneHeight] = useState(0);

  const campaignName = useMemo(() => {
    const fromState = location.state?.campaignName;
    if (fromState) return fromState;
    if (campaignId) return decodeURIComponent(String(campaignId).replace(/\+/g, ' '));
    return 'Campaign';
  }, [location.state, campaignId]);

  const campaignStatusFromState = useMemo(
    () => normalizeCampaignStatus(location.state?.campaignStatus),
    [location.state]
  );

  const { data: campaignStatusFromApi = '' } = useQuery({
    queryKey: ['whatsappCampaignStatus', campaignId],
    queryFn: async () => {
      const rows = await getWhatsAppCampaigns();
      const match = Array.isArray(rows)
        ? rows.find((row) => String(row.id) === String(campaignId))
        : null;
      return normalizeCampaignStatus(match?.status);
    },
    enabled: Boolean(campaignId) && !campaignStatusFromState
  });

  const campaignStatus = campaignStatusFromState || campaignStatusFromApi;
  const isDraftCampaign = campaignStatus === 'draft';

  const numericCampaignId = useMemo(() => {
    const n = Number.parseInt(String(campaignId), 10);
    return Number.isFinite(n) ? n : null;
  }, [campaignId]);

  const [startCampaignModalOpen, setStartCampaignModalOpen] = useState(false);
  const [runHistoryModalOpen, setRunHistoryModalOpen] = useState(false);
  const [selectedPreLeadListId, setSelectedPreLeadListId] = useState('');
  const [selectedPreLeadIds, setSelectedPreLeadIds] = useState([]);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');

  const selectedPreLeadListNumericId = useMemo(() => {
    const n = Number.parseInt(String(selectedPreLeadListId), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedPreLeadListId]);

  const { data: modalListPreLeads = [], isLoading: isModalPreLeadsLoading } = useQuery({
    queryKey: ['preLeadsForStartCampaign', selectedPreLeadListNumericId],
    queryFn: async () => {
      const listId = selectedPreLeadListNumericId;
      const limit = 100;
      let page = 1;
      let totalPages = 1;
      const aggregated = [];
      while (page <= totalPages) {
        const { preLeads, meta } = await getPreLeadsByListId({ preLeadListId: listId, page, limit });
        if (Array.isArray(preLeads) && preLeads.length) aggregated.push(...preLeads);
        totalPages = Number(meta?.totalPages) || 1;
        page += 1;
        if (page > 500) break;
      }
      return aggregated;
    },
    enabled: Boolean(startCampaignModalOpen && selectedPreLeadListNumericId)
  });

  const filteredModalRecipients = useMemo(
    () => filterPreLeadsByRecipientSearch(modalListPreLeads, recipientSearchQuery),
    [modalListPreLeads, recipientSearchQuery]
  );

  const {
    data: templates = [],
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['whatsappTemplates', campaignId],
    queryFn: () => getWhatsAppTemplates(campaignId),
    enabled: Boolean(campaignId)
  });

  const { data: remoteQuestionsBundle } = useQuery({
    queryKey: ['whatsappQuestions', campaignId],
    queryFn: () => getWhatsAppQuestions(campaignId),
    enabled: Boolean(campaignId) && Number.isFinite(Number.parseInt(String(campaignId), 10)),
    retry: false
  });
  const remoteQuestionRows = remoteQuestionsBundle?.questions;
  const remoteQuestionsCategory = remoteQuestionsBundle?.category;

  const { data: preLeadListsData = { preLeadList: [] }, isLoading: isPreLeadListsLoading } = useQuery({
    queryKey: ['preLeadLists'],
    queryFn: getPreLeadLists,
    enabled: startCampaignModalOpen
  });

  const {
    data: campaignRuns = [],
    isLoading: isCampaignRunsLoading,
    isError: isCampaignRunsError,
    error: campaignRunsError,
    refetch: refetchCampaignRuns
  } = useQuery({
    queryKey: ['whatsappCampaignRuns', numericCampaignId],
    queryFn: () => getWhatsAppCampaignRuns(numericCampaignId),
    enabled: Boolean(numericCampaignId && runHistoryModalOpen)
  });

  const sortedCampaignRuns = useMemo(() => {
    if (!Array.isArray(campaignRuns) || campaignRuns.length === 0) return [];
    return [...campaignRuns].sort((a, b) => {
      const ta = new Date(a.started_at || a.completed_at || 0).getTime();
      const tb = new Date(b.started_at || b.completed_at || 0).getTime();
      return tb - ta;
    });
  }, [campaignRuns]);

  const preLeadLists = useMemo(() => {
    if (Array.isArray(preLeadListsData?.preLeadList)) return preLeadListsData.preLeadList;
    if (Array.isArray(preLeadListsData)) return preLeadListsData;
    return [];
  }, [preLeadListsData]);

  const sortedPreLeadLists = useMemo(() => {
    if (!Array.isArray(preLeadLists) || preLeadLists.length === 0) return [];
    return [...preLeadLists].sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      if (tb !== ta) return tb - ta;
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
  }, [preLeadLists]);

  const preLeadListSelectOptions = useMemo(
    () =>
      sortedPreLeadLists.map((list) => ({
        value: String(list.id),
        label: String(list.name || `List #${list.id}`).trim() || `List #${list.id}`
      })),
    [sortedPreLeadLists]
  );

  const preLeadListSelectValue = useMemo(
    () => preLeadListSelectOptions.find((o) => o.value === selectedPreLeadListId) ?? null,
    [preLeadListSelectOptions, selectedPreLeadListId]
  );

  const primaryTemplate = templates[0] ?? null;
  const primaryTemplateNumericId = useMemo(
    () => resolveWhatsAppTemplateId(primaryTemplate),
    [primaryTemplate]
  );

  const templateTitle = useMemo(() => {
    if (!primaryTemplate) {
      return isLoading ? 'Loading…' : 'No template yet';
    }
    const raw =
      primaryTemplate.display_name ??
      primaryTemplate.displayName ??
      primaryTemplate.name ??
      primaryTemplate.template_name;
    if (!raw) return 'Template';
    return String(raw).replace(/_/g, ' ');
  }, [primaryTemplate, isLoading]);

  const templateStatusRaw = useMemo(() => {
    if (!primaryTemplate) return '';
    return (
      primaryTemplate.status ??
      primaryTemplate.template_status ??
      primaryTemplate.approval_status ??
      ''
    );
  }, [primaryTemplate]);

  const { introBody: templateIntroBody, buttons: templateIntroButtons } = useMemo(
    () => parseTemplateForPreview(primaryTemplate),
    [primaryTemplate]
  );

  const [questions, setQuestions] = useState(DEFAULT_QUESTIONS);
  const [questionnaireSaved, setQuestionnaireSaved] = useState(false);
  /** After first successful POST or when questions already exist on the server — next save uses PUT. */
  const [questionnairePersistedOnServer, setQuestionnairePersistedOnServer] = useState(false);
  const [questionnaireCategory, setQuestionnaireCategory] = useState(
    WHATSAPP_QUESTIONS_CATEGORY_DEFAULT
  );

  const remoteMapped = useMemo(
    () => mapApiQuestionsToLocalState(remoteQuestionRows),
    [remoteQuestionRows]
  );

  useEffect(() => {
    setQuestionnairePersistedOnServer(false);
    setQuestionnaireCategory(WHATSAPP_QUESTIONS_CATEGORY_DEFAULT);
  }, [campaignId]);

  useEffect(() => {
    if (remoteQuestionsCategory != null && String(remoteQuestionsCategory).trim()) {
      setQuestionnaireCategory(String(remoteQuestionsCategory).trim());
    }
  }, [remoteQuestionsCategory, campaignId]);

  useEffect(() => {
    if (remoteMapped != null) setQuestionnairePersistedOnServer(true);
  }, [remoteMapped]);

  useEffect(() => {
    if (!campaignId || remoteMapped == null) return;
    if (hydratedCampaignRef.current === campaignId) return;
    hydratedCampaignRef.current = campaignId;
    setQuestions(remoteMapped);
    setQuestionnaireSaved(true);
  }, [campaignId, remoteMapped]);

  const saveMutation = useMutation({
    mutationFn: ({ payload, isUpdate }) =>
      isUpdate ? updateWhatsAppQuestions(payload) : createWhatsAppQuestions(payload),
    onSuccess: (_data, variables) => {
      if (!variables.isUpdate) {
        hydratedCampaignRef.current = null;
      }
      queryClient.invalidateQueries({ queryKey: ['whatsappQuestions', campaignId] });
      setQuestionnairePersistedOnServer(true);
      setQuestionnaireSaved(true);
      toast.success('Questionnaire saved.');
    },
    onError: (err) => {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(typeof msg === 'string' ? msg : 'Could not save questionnaire.');
    }
  });

  const startCampaignMutation = useMutation({
    mutationFn: ({
      campaignId: numericCampaignId,
      pre_lead_list_id,
      whatsapp_template_id,
      pre_lead_ids
    }) =>
      startWhatsAppCampaign(numericCampaignId, {
        pre_lead_list_id,
        whatsapp_template_id,
        pre_lead_ids
      }),
    onSuccess: () => {
      setStartCampaignModalOpen(false);
      setSelectedPreLeadListId('');
      setSelectedPreLeadIds([]);
      setRecipientSearchQuery('');
      toast.success('Campaign started.');
      mixpanel.track('WhatsApp Lead Campaign Started', {
        user_id: userId,
        campaign_id: campaignId
      });
      if (numericCampaignId != null) {
        queryClient.invalidateQueries({ queryKey: ['whatsappCampaignRuns', numericCampaignId] });
      }
      setRunHistoryModalOpen(true);
    },
    onError: (err) => {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(typeof msg === 'string' ? msg : 'Could not start campaign.');
    }
  });

  const handleOpenStartCampaignModal = useCallback(() => {
    const id = Number.parseInt(String(campaignId), 10);
    if (!Number.isFinite(id)) {
      toast.error('Invalid campaign.');
      return;
    }
    if (!questionnairePersistedOnServer) {
      toast.error('Save your questionnaire before starting the campaign.');
      return;
    }
    if (primaryTemplateNumericId == null) {
      toast.error('A WhatsApp template is required before starting the campaign.');
      return;
    }
    setSelectedPreLeadListId('');
    setSelectedPreLeadIds([]);
    setRecipientSearchQuery('');
    setStartCampaignModalOpen(true);
  }, [campaignId, questionnairePersistedOnServer, primaryTemplateNumericId]);

  const handleConfirmStartCampaign = useCallback(() => {
    const cid = Number.parseInt(String(campaignId), 10);
    const preId = Number.parseInt(String(selectedPreLeadListId), 10);
    if (!Number.isFinite(cid)) {
      toast.error('Invalid campaign.');
      return;
    }
    if (!Number.isFinite(preId)) {
      toast.error('Select a pre-lead list.');
      return;
    }
    const preLeadIds = selectedPreLeadIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (preLeadIds.length === 0) {
      toast.error('Select at least one pre-lead to send to.');
      return;
    }
    if (primaryTemplateNumericId == null) {
      toast.error('Missing WhatsApp template.');
      return;
    }
    startCampaignMutation.mutate({
      campaignId: cid,
      pre_lead_list_id: preId,
      whatsapp_template_id: primaryTemplateNumericId,
      pre_lead_ids: preLeadIds
    });
  }, [
    campaignId,
    primaryTemplateNumericId,
    selectedPreLeadListId,
    selectedPreLeadIds,
    startCampaignMutation
  ]);

  const togglePreLeadSelection = useCallback((rawId) => {
    const n = Number(rawId);
    if (!Number.isFinite(n) || n <= 0) return;
    setSelectedPreLeadIds((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
  }, []);

  const selectAllModalPreLeads = useCallback(() => {
    const ids = filteredModalRecipients
      .map((p) => Number(p?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    setSelectedPreLeadIds(ids);
  }, [filteredModalRecipients]);

  const clearPreLeadSelection = useCallback(() => setSelectedPreLeadIds([]), []);

  const patchQuestion = useCallback((id, patch) => {
    if (!isDraftCampaign) {
      toast.error('Questions can be edited only when campaign is in Draft status.');
      return;
    }
    if (
      Array.isArray(patch.options) &&
      (patch.options.length < MIN_QUICK_REPLY_OPTIONS ||
        patch.options.length > MAX_QUICK_REPLY_OPTIONS)
    )
      return;
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  }, [isDraftCampaign]);

  const removeQuestion = useCallback((id) => {
    if (!isDraftCampaign) {
      toast.error('Questions can be edited only when campaign is in Draft status.');
      return;
    }
    setQuestions((prev) => (prev.length <= 1 ? prev : prev.filter((q) => q.id !== id)));
  }, [isDraftCampaign]);

  const addQuestion = useCallback(() => {
    if (!isDraftCampaign) {
      toast.error('Questions can be edited only when campaign is in Draft status.');
      return;
    }
    setQuestions((prev) => [
      ...prev,
      {
        id: createQuestionId(),
        serverId: null,
        prompt: 'New question',
        options: [
          { label: 'Option 1', serverId: null },
          { label: 'Option 2', serverId: null }
        ]
      }
    ]);
  }, [isDraftCampaign]);

  const handleSaveQuestionnaire = useCallback(() => {
    if (!isDraftCampaign) {
      toast.error('Questions can be edited only when campaign is in Draft status.');
      return;
    }
    const id = Number.parseInt(String(campaignId), 10);
    if (!Number.isFinite(id)) {
      toast.error('Invalid campaign.');
      return;
    }
    mixpanel.track('WhatsApp Lead Questionnaire Save', {
      user_id: userId,
      campaign_id: campaignId,
      question_count: questions.length
    });
    saveMutation.mutate({
      payload: questionnairePersistedOnServer
        ? buildWhatsAppQuestionsBulkPayload(id, questions, questionnaireCategory)
        : buildWhatsAppQuestionsPayload(id, questions, questionnaireCategory),
      isUpdate: questionnairePersistedOnServer
    });
  }, [
    campaignId,
    questions,
    questionnaireCategory,
    questionnairePersistedOnServer,
    saveMutation,
    userId,
    isDraftCampaign
  ]);

  const handleEditQuestionnaire = useCallback(() => {
    if (!isDraftCampaign) {
      toast.error('Questions can be edited only when campaign is in Draft status.');
      return;
    }
    setQuestionnaireSaved(false);
  }, [isDraftCampaign]);

  const previewInitials = 'HS';
  const previewBarTitle =
    templateTitle === 'Loading…' || templateTitle === 'No template yet'
      ? campaignName
      : templateTitle;

  useEffect(() => {
    mixpanel.track('WhatsApp Lead Questionnaire Viewed', {
      user_id: userId,
      campaign_id: campaignId
    });
  }, [userId, campaignId]);

  useEffect(() => {
    if (location.state?.openRunHistory) {
      setRunHistoryModalOpen(true);
    }
  }, [location.state?.openRunHistory]);

  useEffect(() => {
    if (!runHistoryModalOpen || !isCampaignRunsError) return;
    const msg = campaignRunsError?.response?.data?.message ?? campaignRunsError?.message;
    toast.error(typeof msg === 'string' ? msg : 'Could not load campaign history.');
  }, [runHistoryModalOpen, isCampaignRunsError, campaignRunsError]);

  useEffect(() => {
    const node = questionnairePaneRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const updateHeight = () => {
      const next = Math.ceil(node.getBoundingClientRect().height || 0);
      setQuestionnairePaneHeight((prev) => (prev === next ? prev : next));
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isError) {
      const msg = error?.response?.data?.message ?? error?.message;
      toast.error(typeof msg === 'string' ? msg : 'Could not load templates.');
    }
  }, [isError, error]);

  const introBody = templateIntroBody;
  const introButtons = templateIntroButtons;

  const canStartWhatsAppCampaign =
    questionnaireSaved &&
    questionnairePersistedOnServer &&
    primaryTemplateNumericId != null &&
    String(templateStatusRaw).toUpperCase() === 'APPROVED';

  return (
    <>
      <Loader
        isVisible={isLoading || startCampaignMutation.isPending}
      />
      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            backArrow
            onBack={() => navigate(basePath)}
            data={{
              name: campaignName,
              subtitle: 'Build your WhatsApp lead flow step by step'
            }}
          />
        </div>
        <div
          className={`fixed z-[9] top-[4.75rem] flex items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-3 shadow-sm transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <div className="flex min-w-0 flex-1 items-center gap-3 pl-11">
            <div className="flex min-w-0 flex-row items-center gap-1.5">
              <p
                className="min-w-0 truncate text-xl font-bold leading-tight tracking-tight text-black antialiased"
                title={templateTitle}>
                {templateTitle}
              </p>
              {templateStatusRaw ? (
                <span
                  className={`inline-flex w-fit shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusBadgeClass(
                    templateStatusRaw
                  )}`}>
                  {String(templateStatusRaw).replace(/_/g, ' ')}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isDraftCampaign && !questionnaireSaved ? (
              <>
                <button
                  type="button"
                  onClick={addQuestion}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50">
                  <Plus className="h-4 w-4" />
                  Add question
                </button>
                {questionnairePersistedOnServer ? (
                  <button
                    type="button"
                    onClick={() => setQuestionnaireSaved(true)}
                    disabled={saveMutation.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60">
                    <X className="h-4 w-4" />
                    Cancel
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={handleSaveQuestionnaire}
                  disabled={saveMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#346C75] bg-[#346C75] px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#2d5c63] disabled:cursor-not-allowed disabled:opacity-60">
                  <Save className="h-4 w-4" />
                  {saveMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : null}
            {isDraftCampaign && questionnaireSaved ? (
              <button
                type="button"
                onClick={handleEditQuestionnaire}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50">
                <Pencil className="h-4 w-4" />
                Edit
              </button>
            ) : null}
            {!isDraftCampaign && !canStartWhatsAppCampaign ? (
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
                View only
              </span>
            ) : null}
            {numericCampaignId != null ? (
              <button
                type="button"
                onClick={() => setRunHistoryModalOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50">
                <History className="h-4 w-4" />
                History
              </button>
            ) : null}
            {canStartWhatsAppCampaign ? (
              <button
                type="button"
                onClick={handleOpenStartCampaignModal}
                disabled={startCampaignMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-700 bg-emerald-700 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60">
                <Play className="h-4 w-4" fill="currentColor" />
                Start campaign
              </button>
            ) : null}
          </div>
        </div>
        <div
          className="flex flex-col pb-16"
          style={{ backgroundColor: PAGE_BG, marginTop: 'calc(4.75rem + 3.5rem)' }}>
          <div className="flex flex-col lg:flex-row lg:items-start">
            {/* Questionnaire */}
            <div ref={questionnairePaneRef} className="min-w-0 flex-1 px-6 py-6">
              <div className="mx-auto max-w-3xl space-y-5">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Lead questions</h2>
                  <p className="mt-0.5 text-sm text-gray-500">
                    {isDraftCampaign
                      ? 'Edit questions and quick replies — preview updates on the right.'
                      : 'Campaign is not in draft. Questions are view-only.'}
                  </p>
                </div>
                <div className="space-y-5">
                  {questions.map((q, i) => (
                    <QuestionCard
                      key={q.id}
                      index={i}
                      question={q}
                      readOnly={questionnaireSaved || !isDraftCampaign}
                      onPatch={(patch) => patchQuestion(q.id, patch)}
                      onRemove={() => removeQuestion(q.id)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="hidden w-px shrink-0 self-stretch bg-gray-200 lg:block" aria-hidden />
            {/* Preview */}
            <div
              className="flex min-h-[620px] w-full shrink-0 flex-col overflow-hidden lg:h-[var(--questionnaire-height)] lg:min-h-[560px] lg:w-[min(100%,420px)] lg:max-w-[40vw]"
              style={
                questionnairePaneHeight > 0
                  ? { '--questionnaire-height': `${questionnairePaneHeight}px` }
                  : undefined
              }>
              <QuestionnaireWhatsAppPreview
                barTitle={previewBarTitle}
                initials={previewInitials}
                introBody={introBody}
                introButtons={introButtons}
                questions={questions}
              />
            </div>
          </div>
        </div>
      </Layout>

      {startCampaignModalOpen ? (
        <Modal
          name="Start campaign"
          width={52}
          onClose={() => {
            if (startCampaignMutation.isPending) return;
            setStartCampaignModalOpen(false);
            setSelectedPreLeadListId('');
            setSelectedPreLeadIds([]);
            setRecipientSearchQuery('');
          }}>
          <p className="text-sm text-gray-600 mb-4">
            Choose a pre-lead list, then pick which contacts in that list should receive this
            campaign. The active WhatsApp template for this campaign is sent automatically.
          </p>
          <label
            htmlFor="wa-start-prelead"
            className="block text-sm font-medium text-gray-800 mb-1.5">
            Pre-lead list
          </label>
          {isPreLeadListsLoading ? (
            <p className="mb-4 text-sm text-gray-500">Loading pre-lead lists…</p>
          ) : null}
          <div className="mb-4">
            <Select
              inputId="wa-start-prelead"
              instanceId="wa-start-prelead-list"
              aria-label="Pre-lead list"
              isDisabled={startCampaignMutation.isPending || isPreLeadListsLoading}
              isLoading={isPreLeadListsLoading}
              options={preLeadListSelectOptions}
              value={preLeadListSelectValue}
              onChange={(opt) => {
                setSelectedPreLeadListId(opt?.value ?? '');
                setSelectedPreLeadIds([]);
                setRecipientSearchQuery('');
              }}
              placeholder="Select a list…"
              isClearable={false}
              isSearchable
              maxMenuHeight={220}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              styles={startCampaignPreLeadListSelectStyles}
              className="text-sm"
            />
          </div>
          {!isPreLeadListsLoading && sortedPreLeadLists.length === 0 ? (
            <p className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              No pre-lead lists found. Create one under Sales → Prelead List, then try again.
            </p>
          ) : null}
          {selectedPreLeadListNumericId ? (
            <div className="mb-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                <label className="block text-sm font-medium text-gray-800">Recipients</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={
                      startCampaignMutation.isPending ||
                      isModalPreLeadsLoading ||
                      !filteredModalRecipients.length
                    }
                    onClick={selectAllModalPreLeads}
                    className="text-xs font-medium text-[#346C75] hover:underline disabled:opacity-50 disabled:no-underline">
                    Select all
                  </button>
                  <button
                    type="button"
                    disabled={startCampaignMutation.isPending || selectedPreLeadIds.length === 0}
                    onClick={clearPreLeadSelection}
                    className="text-xs font-medium text-gray-600 hover:underline disabled:opacity-50 disabled:no-underline">
                    Clear
                  </button>
                </div>
              </div>
              {isModalPreLeadsLoading ? (
                <p className="text-sm text-gray-500 py-2">Loading contacts…</p>
              ) : modalListPreLeads.length === 0 ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  No pre-leads in this list. Add contacts to the list, then try again.
                </p>
              ) : (
                <>
                  <div className="relative mb-2">
                    <Search
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                      aria-hidden
                    />
                    <input
                      id="wa-recipient-search"
                      type="search"
                      autoComplete="off"
                      value={recipientSearchQuery}
                      onChange={(e) => setRecipientSearchQuery(e.target.value)}
                      placeholder="Search by name, phone, or email…"
                      aria-label="Search recipients"
                      disabled={startCampaignMutation.isPending}
                      className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none ring-[#346C75]/25 placeholder:text-gray-400 focus:border-[#346C75] focus:ring-2 disabled:opacity-60"
                    />
                  </div>
                  {filteredModalRecipients.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2 rounded-lg border border-gray-200 bg-gray-50 px-3">
                      No contacts match your search.
                    </p>
                  ) : (
                    <ul
                      className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/80 divide-y divide-gray-200">
                      {filteredModalRecipients.map((p) => {
                        const id = Number(p?.id);
                        const checked = Number.isFinite(id) && selectedPreLeadIds.includes(id);
                        const name = String(p?.name ?? '').trim() || `Pre-lead #${id}`;
                        const phone = [p?.countryCode, p?.phone].filter(Boolean).join(' ').trim() || '—';
                        return (
                          <li key={id} className="flex items-start gap-3 px-3 py-2.5 bg-white">
                            <input
                              type="checkbox"
                              id={`wa-start-prelead-${id}`}
                              checked={checked}
                              disabled={startCampaignMutation.isPending || !Number.isFinite(id)}
                              onChange={() => togglePreLeadSelection(id)}
                              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#346C75] focus:ring-[#346C75]"
                            />
                            <label
                              htmlFor={`wa-start-prelead-${id}`}
                              className="min-w-0 flex-1 cursor-pointer text-sm text-gray-900">
                              <span className="font-medium">{name}</span>
                              <span className="block text-xs text-gray-500">{phone}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
              {selectedPreLeadIds.length > 0 ? (
                <p className="mt-2 text-xs text-gray-500">
                  {selectedPreLeadIds.length} contact{selectedPreLeadIds.length === 1 ? '' : 's'} selected
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button
              type="button"
              disabled={startCampaignMutation.isPending}
              onClick={() => {
                setStartCampaignModalOpen(false);
                setSelectedPreLeadListId('');
                setSelectedPreLeadIds([]);
                setRecipientSearchQuery('');
              }}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button
              type="button"
              disabled={
                startCampaignMutation.isPending ||
                !selectedPreLeadListId ||
                selectedPreLeadIds.length === 0 ||
                primaryTemplateNumericId == null
              }
              onClick={handleConfirmStartCampaign}
              className="rounded-lg border border-emerald-700 bg-emerald-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50">
              {startCampaignMutation.isPending ? 'Starting…' : 'Start campaign'}
            </button>
          </div>
        </Modal>
      ) : null}

      {runHistoryModalOpen ? (
        <Modal name="Campaign send history" width={56} onClose={() => setRunHistoryModalOpen(false)}>
          <p className="mb-4 text-sm text-gray-600">
            Each run is one send batch to a pre-lead list using this campaign&apos;s WhatsApp template.
          </p>
          {isCampaignRunsLoading ? (
            <p className="py-8 text-center text-sm text-gray-500">Loading send history…</p>
          ) : sortedCampaignRuns.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
              No sends yet. Start the campaign to message your pre-leads.
            </p>
          ) : (
            <div className="max-h-[min(70vh,32rem)] overflow-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="sticky top-0 z-[1] bg-gray-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Run
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Started
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      List
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Status
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Recipients
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Sent
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Failed
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
              {sortedCampaignRuns.map((run) => {
                const summary = getRunDeliverySummary(run);
                return (
                  <tr key={run.id} className="hover:bg-gray-50/80">
                    <td className="whitespace-nowrap px-3 py-2.5 font-medium text-gray-900 tabular-nums">
                      #{run.id}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-gray-600">
                      {formatRunDateTime(run.started_at)}
                    </td>
                    <td
                      className="max-w-[14rem] truncate px-3 py-2.5 text-gray-800"
                      title={
                        run.pre_lead_list_id != null ? `List #${run.pre_lead_list_id}` : undefined
                      }>
                      {run.pre_lead_list?.name?.trim() ||
                        (run.pre_lead_list_id != null ? `List #${run.pre_lead_list_id}` : '—')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <span
                        className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${runStatusBadgeClass(
                          run.status
                        )}`}>
                        {String(run.status || 'unknown').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-900">
                      {summary.total}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums font-medium text-emerald-700">
                      {summary.sent}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-rose-700">
                      {summary.failed}
                    </td>
                  </tr>
                );
              })}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={() => refetchCampaignRuns()}
              disabled={isCampaignRunsLoading}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50">
              Refresh
            </button>
            <button
              type="button"
              onClick={() => setRunHistoryModalOpen(false)}
              className="rounded-lg border border-[#346C75] bg-[#346C75] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#2d5c63]">
              Close
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default WhatsAppLeadQuestionnaire;
